import { useState } from 'react';

import {
  AppIcon,
  PopoverFilterMenu,
  PopoverFilterMenuItem,
  FilterCategoryConfig,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Button, SxProps, TextField, Theme, Typography } from '@mui/material';

const SECTION_HEADER_STRINGS = appStrings.modules.priorities.sectionHeader;

type SectionHeaderProps = Readonly<{
  title: string;
  description: string;
  searchPlaceholder: string;

  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;

  // Filter props - either simple or hierarchical
  filterMenuItems?: PopoverFilterMenuItem[];
  popoverFilterCategories?: FilterCategoryConfig[];
  filterTitle?: string;
  hasActiveFilters?: boolean;
  clearFilters?: () => void;
  isFilteredResultEmpty?: boolean;
  disabled?: boolean;
  inline?: boolean;
}>;

export const SectionHeader = ({
  title,
  description,
  searchPlaceholder,
  searchTerm,
  setSearchTerm,
  filterMenuItems,
  popoverFilterCategories,
  filterTitle,
  hasActiveFilters = false,
  clearFilters,
  isFilteredResultEmpty = false,
  disabled = false,
  inline = false,
}: SectionHeaderProps) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);

  const hasFilterItems =
    (filterMenuItems && filterMenuItems.length > 0) ||
    (popoverFilterCategories && popoverFilterCategories.length > 0);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hasFilterItems) {
      setFilterAnchorEl(event.currentTarget);
    }
  };

  return (
    <Box sx={getTitleFilterContainerSx(inline)}>
      <Box sx={titleContainerSx}>
        <Typography variant="text-lg-semibold">{title}</Typography>

        <Typography variant="text-sm-regular">{description}</Typography>
      </Box>

      <Box width="100%">
        <Box sx={getSearchFilterContainerSx(inline)}>
          <TextField
            id="priorities-section-search-input"
            variant="outlined"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            InputProps={{
              startAdornment: (
                <AppIcon
                  iconName="SearchLg"
                  color="Colors/Foreground/fg-quaternary"
                  diameter="20px"
                />
              ),
              endAdornment: (
                <Box sx={clearSearchButtonSx} onClick={() => setSearchTerm('')}>
                  <AppIcon
                    iconName="XClose"
                    color={
                      searchTerm ? 'Colors/Foreground/fg-quaternary' : 'Colors/Base/Transparent'
                    }
                    diameter="16px"
                  />
                </Box>
              ),
            }}
            helperText={
              isFilteredResultEmpty && (
                <Typography
                  variant="text-sm-regular"
                  color={({ backrs }) => backrs.colors['Colors/Text/text-placeholder']}
                >
                  {SECTION_HEADER_STRINGS.searchError}
                </Typography>
              )
            }
            sx={searchInputStyle}
            disabled={disabled}
          />

          <Button
            id="priorities-section-filter-button"
            hierarchy="Secondary gray"
            size="lg"
            sx={filterButtonStyle(!!hasFilterItems)}
            onClick={handleFilterClick}
            startIcon={
              <AppIcon
                iconName="FilterFunnel01"
                color="Colors/Text/text-secondary"
                diameter="20px"
                fill={hasActiveFilters ? 'Colors/Text/text-secondary' : undefined}
              />
            }
            disabled={disabled}
          >
            {SECTION_HEADER_STRINGS.filter}
          </Button>
        </Box>
      </Box>

      {hasFilterItems && (
        <PopoverFilterMenu
          anchorEl={filterAnchorEl}
          setAnchorEl={setFilterAnchorEl}
          categories={popoverFilterCategories || []}
          clearFilters={clearFilters || (() => undefined)}
          title={filterTitle}
        />
      )}
    </Box>
  );
};

const titleContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const getTitleFilterContainerSx =
  (inline: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    containerType: 'inline-size',
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: backrs.spacing['spacing-2xl'],

    borderBottom: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    paddingBottom: backrs.spacing['spacing-2xl'],
    width: '100%',
    flex: 1,
    '@container (min-width: 600px)': {
      flexWrap: inline ? 'nowrap' : 'wrap',
    },
    ...(!inline && {
      '@container (min-width: 900px)': {
        flexWrap: 'nowrap',
      },
    }),
  });

const getSearchFilterContainerSx =
  (inline: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    gap: backrs.spacing['spacing-xl'],
    paddingBottom: backrs.spacing['spacing-none'],
    flexDirection: 'column',
    width: '100%',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    '@container (min-width: 600px)': {
      flexDirection: 'row',
      ...(inline && {
        paddingBottom: backrs.spacing['spacing-sm'],
        justifyContent: 'flex-end',
      }),
    },
    ...(!inline && {
      '@container (min-width: 900px)': {
        paddingBottom: backrs.spacing['spacing-sm'],
        justifyContent: 'flex-end',
      },
    }),
  });

const searchInputStyle: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '100%',
  minWidth: '100%',
  '@container (min-width: 600px)': {
    maxWidth: '320px',
    minWidth: '320px',
  },
  minHeight: '44px',
  height: '44px',
  maxHeight: '44px',
  '& .MuiOutlinedInput-root': {
    maxHeight: '44px',
    minHeight: '44px',
    height: '44px !important',
    borderColor: backrs.colors['Colors/Foreground/fg-quaternary'],
    '&.Mui-disabled': {
      borderColor: backrs.colors['Colors/Border/border-disabled'],
      '& fieldset': {
        borderColor: backrs.colors['Colors/Border/border-disabled'],
      },
    },
  },

  '& .MuiInputBase-input': {
    maxHeight: '44px',
    minHeight: '44px',
    height: '44px',
  },

  '& .MuiFormHelperText-root': {
    position: 'absolute',
    bottom: `-${backrs.spacing['spacing-2xl']}`,
    left: backrs.spacing['spacing-lg'],
    margin: backrs.spacing['spacing-none'],
  },
});

const filterButtonStyle =
  (hasFilterMenuItems: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: hasFilterMenuItems ? 'flex' : 'none',
    maxHeight: '44px',
    minHeight: '44px',
    height: '44px',
    borderWidth: '1px',
    alignSelf: 'flex-start',

    '&:active': {
      borderWidth: '1px',
      backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
      borderColor: backrs.colors['Colors/Foreground/fg-quaternary'],
    },
    borderColor: backrs.colors['Colors/Foreground/fg-quaternary'],

    '&.Mui-disabled': {
      borderWidth: '1px',
      borderColor: backrs.colors['Colors/Border/border-disabled'],
      color: backrs.colors['Colors/Text/text-disabled'],
      backgroundColor: backrs.colors['Colors/Background/bg-disabled-subtle'],
    },
  });

const clearSearchButtonSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};
